/**
 * Root route error boundary.
 *
 * Without this, an uncaught render error or a failed lazy-chunk import
 * (routine on venue wifi right after a deploy invalidates the old asset
 * hashes) dropped the user on react-router's unstyled default error screen,
 * or a blank page, with no way back in in-app (persona/UX review #17).
 */

import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import * as Sentry from '@sentry/react';

const CHUNK_RELOAD_KEY = 'bbx-chunk-reload-attempted';

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk .* failed/i.test(message) ||
    // Safari: stale chunk URL after a deploy often returns SPA index.html,
    // which then fails module evaluation with this MIME TypeError.
    /is not a valid JavaScript MIME type/i.test(message)
  );
}

export function RouteErrorPage() {
  const error = useRouteError();
  const [autoReloaded, setAutoReloaded] = useState(false);

  const chunkLoadError = isChunkLoadError(error);
  const status = isRouteErrorResponse(error) ? error.status : undefined;

  // Report genuine render/loader failures to Sentry. Skip 404s (expected) and
  // chunk-load errors (benign — a new build shipped; handled by the reload below).
  useEffect(() => {
    if (chunkLoadError || status === 404) return;
    Sentry.captureException(error);
  }, [error, chunkLoadError, status]);

  useEffect(() => {
    if (!chunkLoadError) return;
    // A stale chunk reference almost always means a new build shipped since
    // this tab loaded — a single silent reload picks up the fresh asset
    // manifest and resolves it without the user ever seeing an error. Guard
    // with sessionStorage so a *genuinely* broken deploy doesn't reload-loop.
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      setAutoReloaded(true);
      return;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.reload();
  }, [chunkLoadError]);

  // Clear the guard once we render successfully elsewhere in the app.
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    };
  }, []);

  const title =
    status === 404
      ? 'Page Not Found'
      : chunkLoadError
        ? 'Update Available'
        : 'Something Went Wrong';
  const message =
    status === 404
      ? "The page you're looking for doesn't exist or has been moved."
      : chunkLoadError
        ? autoReloaded
          ? "We tried refreshing automatically but it didn't resolve. Please reload manually."
          : 'Loading the latest version…'
        : 'An unexpected error occurred. Reloading the page usually fixes this.';

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="text-6xl mb-4">{status === 404 ? '🔍' : '⚠️'}</div>
      <h1 className="text-2xl font-bold text-bbx-dark-100 mb-2">{title}</h1>
      <p className="text-bbx-dark-400 mb-6 max-w-sm">{message}</p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-bbx-primary-600 hover:bg-bbx-primary-500 text-white font-medium rounded-lg transition-colors min-h-tap"
        >
          Reload
        </button>
        <a
          href="/"
          className="inline-flex items-center gap-2 px-4 py-2 bg-bbx-dark-700 hover:bg-bbx-dark-600 text-bbx-dark-200 font-medium rounded-lg transition-colors min-h-tap"
        >
          Go Home
        </a>
      </div>
    </div>
  );
}

export default RouteErrorPage;
